import React, { useState } from 'react';
import {
  Button,
  Card,
  Input,
  Modal,
  NoctuaMark,
  SectionHeader,
  Spinner,
  StatCard,
  StatusBadge,
} from '../components/common';
import EditionCard, { Edition } from '../components/feed/EditionCard';
import FeedSummaryCard from '../components/feed/FeedSummaryCard';
import LibraryList from '../components/library/LibraryList';
import AudioPlayer from '../components/library/AudioPlayer';
import UrlInputForm from '../components/conversion/UrlInputForm';
import TranscriptModal from '../components/conversion/TranscriptModal';
import CreditBalance from '../components/credits/CreditBalance';
import CreditPackCard from '../components/credits/CreditPackCard';
import type { ShareResult } from '../api/conversions';
import type { Conversion, CreditPack } from '../types';

/* ============================================================================
   DesignSystemPage
   A single page that mounts every visual component in the app with mock data.
   Use it to test design changes in isolation, without navigating the full app.
   Public route: /design (no auth required).
   ============================================================================ */

const SECTIONS = [
  { id: 'foundations', label: 'Foundations' },
  { id: 'mark', label: 'Mark' },
  { id: 'atoms', label: 'Atoms' },
  { id: 'overlays', label: 'Overlays' },
  { id: 'conversion', label: 'Conversion' },
  { id: 'credits', label: 'Credits' },
  { id: 'feed', label: 'Feed' },
  { id: 'feeds', label: 'Feeds' },
  { id: 'library', label: 'Library' },
];

/* ---- Mock data ---- */

const sampleEdition: Edition = {
  id: 'edition-1',
  serialNumber: 'NCT-20260414-042',
  editionNumber: 42,
  title: "The hidden cost of platform teams that can't say no",
  author: 'Gergely Orosz',
  source: 'The Pragmatic Engineer',
  sourceUrl: 'https://blog.pragmaticengineer.com',
  durationSeconds: 18 * 60,
  publishedAt: '2026-04-14T08:00:00Z',
  status: 'ready',
  remainingSeconds: 7 * 60 + 2,
};

const sampleEditions: Edition[] = [
  { ...sampleEdition, id: 'e-1', editionNumber: 42, status: 'ready' },
  {
    ...sampleEdition,
    id: 'e-2',
    editionNumber: 41,
    title: 'Agentic systems and the evaluation problem nobody is solving',
    source: 'Anthropic Engineering',
    sourceUrl: 'https://www.anthropic.com/engineering',
    durationSeconds: 24 * 60,
    publishedAt: '2026-04-13T08:00:00Z',
    status: 'ready',
    author: null,
  },
  {
    ...sampleEdition,
    id: 'e-3',
    editionNumber: 40,
    title: 'GoldenGate vs ZDM — what nobody tells you about Oracle migration',
    source: 'blog.glyndarkin.co.uk',
    sourceUrl: 'https://blog.glyndarkin.co.uk',
    durationSeconds: 11 * 60,
    publishedAt: '2026-04-12T08:00:00Z',
    status: 'processing',
    author: 'Glyn Darkin',
  },
];

const sampleConversion = (overrides: Partial<Conversion> = {}): Conversion => ({
  id: 'conv-' + Math.random().toString(36).slice(2, 8),
  userId: 'user-1',
  title: 'The hidden cost of platform teams that can\'t say no',
  author: 'Gergely Orosz',
  status: 'completed',
  progress: 100,
  sourceUrl: 'https://blog.pragmaticengineer.com/platform-teams',
  url: 'https://blog.pragmaticengineer.com/platform-teams',
  outputFormat: 'mp3',
  audioUrl: null,
  durationSeconds: 18 * 60,
  fileSize: 2_400_000,
  creditsUsed: 1,
  transcript: 'Platform engineering has quietly become the discipline that determines whether software organisations scale or stall…',
  createdAt: '2026-04-14T08:00:00Z',
  updatedAt: '2026-04-14T08:18:00Z',
  completedAt: new Date('2026-04-14T08:18:00Z'),
  ...overrides,
});

const sampleConversions: Conversion[] = [
  sampleConversion({ id: 'c-1', title: "The hidden cost of platform teams that can't say no" }),
  sampleConversion({
    id: 'c-2',
    title: 'Agentic systems and the evaluation problem nobody is solving',
    author: null,
    durationSeconds: 24 * 60,
    createdAt: '2026-04-13T08:00:00Z',
  }),
  sampleConversion({
    id: 'c-3',
    title: 'Why your runbooks rot — and the discipline that keeps them honest',
    author: 'SRE Weekly',
    durationSeconds: 14 * 60,
    createdAt: '2026-04-11T21:03:00Z',
  }),
];

const samplePacks: CreditPack[] = [
  {
    id: 'starter',
    name: 'Starter',
    credits: 10,
    pricePence: 500,
    priceDisplay: '5.00',
    description: 'A first taste — perfect to see if noctua fits your reading habit.',
    popular: false,
  },
  {
    id: 'regular',
    name: 'Regular',
    credits: 50,
    pricePence: 2000,
    priceDisplay: '20.00',
    description: 'Enough to convert a season of your favourite newsletter.',
    popular: true,
  },
  {
    id: 'serious',
    name: 'Serious',
    credits: 200,
    pricePence: 7000,
    priceDisplay: '70.00',
    description: 'For long-form readers building a substantial collection.',
    popular: false,
  },
];

const sampleTranscript = `<p>Platform engineering has quietly become the discipline that determines whether software organisations scale or stall. The teams who get it right build trust by deflecting work; the teams who get it wrong burn out by absorbing it.</p>
<p>Three patterns separate the two. The first is the willingness to say no — and to say it precisely, with a written rationale. The second is investment in self-service: every paved-path tool removes a future support ticket. The third is product-mindedness: the platform is a product, its users are engineers, and dogfooding is non-negotiable.</p>
<p>What's striking, talking to dozens of platform leads, is how rarely the failing teams recognise the pattern. They've internalised "we exist to help" so deeply that "no" feels like a failure of mission rather than a load-bearing tool.</p>`;

/* ---- Layout helpers ---- */

function Section({
  id,
  title,
  subtitle,
  children,
}: {
  id: string;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <section id={id} className="border-t border-border pt-14 pb-12 scroll-mt-20">
      <SectionHeader
        eyebrow={id}
        title={title}
        description={subtitle}
        level="h2"
        size="lg"
        className="mb-10"
      />
      {children}
    </section>
  );
}

function SubBlock({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="mb-10 last:mb-0">
      <p className="font-mono text-xs tracking-[0.14em] uppercase text-muted-foreground mb-4">
        {label}
      </p>
      {children}
    </div>
  );
}

function Swatch({
  name,
  cssVar,
  className,
  textOn,
}: {
  name: string;
  cssVar: string;
  className: string;
  textOn?: 'dark' | 'light';
}) {
  return (
    <div className="bg-card border border-border rounded-sm overflow-hidden">
      <div className={`${className} h-20 flex items-end p-3`}>
        <span
          className={`font-mono text-xs tracking-[0.08em] ${
            textOn === 'light' ? 'text-black' : 'text-white/70'
          }`}
        >
          {cssVar}
        </span>
      </div>
      <div className="p-3 border-t border-border">
        <div className="font-mono text-xs text-foreground">{name}</div>
      </div>
    </div>
  );
}

/* ============================================================================ */

export function DesignSystemPage() {
  const [modalOpen, setModalOpen] = useState(false);
  const [transcriptOpen, setTranscriptOpen] = useState(false);
  const [playerOpen, setPlayerOpen] = useState(false);

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Top chrome — sticky nav with section jump links */}
      <header className="sticky top-0 z-30 bg-background/95 backdrop-blur border-b border-border">
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <NoctuaMark size={22} />
            <span className="font-display text-base font-bold lowercase tracking-[-0.03em]">
              noctua
              <sup className="font-medium text-muted-foreground tracking-normal ml-0.5 text-[0.5em] align-super">
                ™
              </sup>
            </span>
            <span className="font-mono text-xs tracking-[0.18em] uppercase text-muted-foreground border-l border-border pl-3 ml-1">
              design system
            </span>
          </div>
          <nav className="hidden md:flex items-center gap-5">
            {SECTIONS.map((s) => (
              <a
                key={s.id}
                href={`#${s.id}`}
                className="font-mono text-xs tracking-[0.14em] lowercase text-muted-foreground hover:text-foreground transition-colors"
              >
                {s.label.toLowerCase()}
              </a>
            ))}
          </nav>
        </div>
      </header>

      {/* Page intro */}
      <div className="max-w-6xl mx-auto px-6 pt-16 pb-4">
        <p className="font-mono text-xs tracking-[0.18em] lowercase text-muted-foreground flex items-center gap-2.5 mb-4">
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-primary shadow-[0_0_6px_hsl(var(--primary))]" />
          field system / v2.0
        </p>
        <h1 className="font-display text-5xl md:text-6xl font-bold lowercase tracking-[-0.04em] leading-none">
          design system
        </h1>
        <p className="mt-6 max-w-2xl text-sm text-muted-foreground leading-relaxed">
          Every component in the app, rendered with mock data on a single page. Use this surface to
          test design tokens, type, colour and component-level changes in isolation — without
          touching the live app shell.
        </p>
        <p className="mt-3 font-mono text-xs tracking-[0.06em] text-muted-foreground">
          Edit <span className="text-foreground">frontend/src/styles/tokens.css</span> or{' '}
          <span className="text-foreground">frontend/src/index.css</span> and the page reloads.
        </p>
      </div>

      <main className="max-w-6xl mx-auto px-6 pb-32">
        {/* ============================== FOUNDATIONS ============================== */}
        <Section
          id="foundations"
          title="foundations"
          subtitle="Surface, accents, type and radii. Everything else composes from these."
        >
          <SubBlock label="surface palette">
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-2">
              <Swatch name="black" cssVar="#0A0A0A" className="bg-[#0a0a0a]" />
              <Swatch name="carbon" cssVar="#161616" className="bg-[#161616]" />
              <Swatch name="graphite" cssVar="#1E1E1E" className="bg-[#1e1e1e]" />
              <Swatch name="steel" cssVar="#2A2A2A" className="bg-[#2a2a2a]" />
              <Swatch name="anodized" cssVar="#3A3A3A" className="bg-[#3a3a3a]" />
              <Swatch name="grey-700" cssVar="#5C5C5C" className="bg-[#5c5c5c]" />
              <Swatch name="grey-500" cssVar="#8A8A8A" className="bg-[#8a8a8a]" />
              <Swatch name="grey-300" cssVar="#BCBCBC" className="bg-[#bcbcbc]" textOn="light" />
              <Swatch name="grey-100" cssVar="#E4E4E4" className="bg-[#e4e4e4]" textOn="light" />
              <Swatch name="white" cssVar="#F4F4F2" className="bg-[#f4f4f2]" textOn="light" />
            </div>
          </SubBlock>

          <SubBlock label="led accents — state only · <5% of surface">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <Swatch name="led-orange / primary" cssVar="#FF5A1F" className="bg-[#ff5a1f]" textOn="light" />
              <Swatch name="led-amber / warn" cssVar="#FFAE00" className="bg-[#ffae00]" textOn="light" />
              <Swatch name="led-green / ready" cssVar="#3AD17A" className="bg-[#3ad17a]" textOn="light" />
              <Swatch name="led-red / error" cssVar="#FF3B30" className="bg-[#ff3b30]" textOn="light" />
            </div>
            <div className="mt-4 flex items-center gap-5 font-mono text-xs tracking-[0.14em] uppercase text-muted-foreground">
              <span className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-[#3ad17a] shadow-[0_0_6px_#3ad17a]" />
                ready
              </span>
              <span className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-[#ffae00] shadow-[0_0_6px_#ffae00]" />
                processing
              </span>
              <span className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-[#ff5a1f] shadow-[0_0_6px_#ff5a1f]" />
                live
              </span>
              <span className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-[#ff3b30] shadow-[0_0_6px_#ff3b30]" />
                error
              </span>
            </div>
          </SubBlock>

          <SubBlock label="type scale">
            <div className="bg-card border border-border rounded-sm divide-y divide-border">
              <TypeRow label="display / 700 / 40px" demoClass="font-display text-[40px] font-bold lowercase leading-none tracking-[-0.03em]">
                portable system
              </TypeRow>
              <TypeRow label="heading / 700 / 22px" demoClass="font-display text-[22px] font-bold lowercase leading-tight tracking-[-0.02em]">
                six stereo channels
              </TypeRow>
              <TypeRow label="body / 400 / 16px" demoClass="font-body text-base leading-relaxed text-foreground">
                A sturdy little machine, encased in CNC aluminium with custom-made encoders and faders.
              </TypeRow>
              <TypeRow label="mono / 400 / 13px / +2% tracking" demoClass="font-mono text-[13px] tracking-[0.02em]">
                24-bit / 96 kHz · ch.01 → main
              </TypeRow>
              <TypeRow label="micro / mono / uppercase / +18% tracking" demoClass="font-mono text-xs tracking-[0.18em] uppercase text-muted-foreground">
                aux · cue · main out
              </TypeRow>
            </div>
          </SubBlock>

          <SubBlock label="radii — 2px / 3px maximum">
            <div className="flex items-end gap-4">
              <div className="text-center">
                <div className="w-20 h-20 bg-card border border-border rounded-none" />
                <span className="block mt-2 font-mono text-xs text-muted-foreground">0</span>
              </div>
              <div className="text-center">
                <div className="w-20 h-20 bg-card border border-border rounded-[2px]" />
                <span className="block mt-2 font-mono text-xs text-muted-foreground">2px / xs</span>
              </div>
              <div className="text-center">
                <div className="w-20 h-20 bg-card border border-border rounded-[3px]" />
                <span className="block mt-2 font-mono text-xs text-muted-foreground">3px / md</span>
              </div>
              <div className="text-center">
                <div className="w-20 h-20 bg-card border border-border rounded-full" />
                <span className="block mt-2 font-mono text-xs text-muted-foreground">full · marks only</span>
              </div>
            </div>
          </SubBlock>
        </Section>

        {/* ============================== MARK ============================== */}
        <Section
          id="mark"
          title="mark"
          subtitle="The owl. Monochrome on the chassis — accent never decorates the mark."
        >
          <div className="bg-card border border-border rounded-sm p-8 flex items-end gap-10">
            {[16, 24, 32, 48, 64].map((s) => (
              <div key={s} className="text-center">
                <NoctuaMark size={s} />
                <span className="block mt-3 font-mono text-xs text-muted-foreground">{s}px</span>
              </div>
            ))}
          </div>
        </Section>

        {/* ============================== ATOMS ============================== */}
        <Section
          id="atoms"
          title="atoms"
          subtitle="Buttons, inputs, cards, spinners — the primitives every page composes from."
        >
          <SubBlock label="buttons / variants × sizes — tx-6 record · shift · select · peak">
            <div className="space-y-4">
              {(['sm', 'md', 'lg'] as const).map((size) => (
                <div key={size} className="flex flex-wrap items-center gap-2.5">
                  <span className="w-8 font-mono text-xs uppercase text-muted-foreground">{size}</span>
                  <Button variant="primary" size={size}>record</Button>
                  <Button variant="secondary" size={size}>shift</Button>
                  <Button variant="outline" size={size}>select</Button>
                  <Button variant="danger" size={size}>peak</Button>
                </div>
              ))}
              <div className="flex flex-wrap items-center gap-2.5 pt-2">
                <span className="w-8 font-mono text-xs uppercase text-muted-foreground">…</span>
                <Button variant="primary" loading>loading</Button>
                <Button variant="primary" disabled>disabled</Button>
              </div>
            </div>
            <p className="mt-5 max-w-2xl font-mono text-xs leading-relaxed text-muted-foreground">
              <span className="text-foreground">primary</span> is the white "record" — the one
              important CTA per surface.{' '}
              <span className="text-foreground">danger</span> is the LED-accent outline ("peak") —
              used for alert / destructive intent. Colour appears only on the peak edge until the
              user commits, then fills.
            </p>
          </SubBlock>

          <SubBlock label="input field">
            <div className="grid md:grid-cols-2 gap-6 max-w-2xl">
              <Input label="Source URL" placeholder="https://example.com/article" />
              <Input
                label="With helper"
                placeholder="me@example.com"
                helperText="We'll send the magic link here."
              />
              <Input
                label="With error"
                placeholder="https://"
                defaultValue="not-a-url"
                error="Enter a valid URL (e.g. https://example.com/article)."
              />
              <Input label="Disabled" placeholder="locked" disabled />
            </div>
          </SubBlock>

          <SubBlock label="card / variants">
            <div className="grid md:grid-cols-3 gap-4">
              <Card>
                <p className="font-display text-base font-bold lowercase mb-2">default</p>
                <p className="text-sm text-muted-foreground">
                  Padded body, hairline warm border, sharp 2px radius.
                </p>
              </Card>
              <Card
                header={<p className="font-display text-sm font-bold lowercase">header / footer</p>}
                footer={<p className="font-mono text-xs uppercase tracking-widest text-muted-foreground">footer · meta</p>}
              >
                <p className="text-sm text-muted-foreground">
                  Header and footer sit inside the card with their own borders.
                </p>
              </Card>
              <Card padding="lg">
                <p className="font-display text-base font-bold lowercase mb-2">padding lg</p>
                <p className="text-sm text-muted-foreground">
                  Padding scale: <code className="font-mono">none · sm · md · lg</code>.
                </p>
              </Card>
            </div>
          </SubBlock>

          <SubBlock label="stat card / dashboard pattern">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <StatCard label="Credit Balance" linkTo="/credits" linkText="buy more credits">
                <div className="flex items-baseline gap-3">
                  <span
                    className="font-mono text-6xl font-normal tracking-[0.04em] leading-none tabular-nums"
                    style={{
                      color: 'var(--color-led-green)',
                      textShadow: '0 0 12px rgba(58, 209, 122, 0.45)',
                    }}
                  >
                    42
                  </span>
                  <span className="font-mono text-xs tracking-[0.18em] uppercase text-muted-foreground">
                    credits
                  </span>
                </div>
              </StatCard>
              <StatCard label="Editions" linkTo="/collection" linkText="view collection">
                <div className="flex items-baseline gap-3">
                  <span
                    className="font-mono text-6xl font-normal tracking-[0.04em] leading-none tabular-nums"
                    style={{
                      color: 'var(--color-led-green)',
                      textShadow: '0 0 12px rgba(58, 209, 122, 0.45)',
                    }}
                  >
                    18
                  </span>
                  <span className="font-mono text-xs tracking-[0.18em] uppercase text-muted-foreground">
                    editions
                  </span>
                </div>
              </StatCard>
              <StatCard label="Podcast Feed" linkTo="/feeds" linkText="get feed URL">
                <p className="font-body text-sm text-muted-foreground">
                  Your personal podcast feed
                </p>
              </StatCard>
            </div>
            <p className="mt-5 max-w-2xl font-mono text-xs leading-relaxed text-muted-foreground">
              Uppercase mono label at top, value (any node — usually an LED readout) in the
              middle, optional link separated by a hairline rule at the bottom. Cards in a grid
              share the same height because the link sits on <code>mt-auto</code>.
            </p>
          </SubBlock>

          <SubBlock label="spinner / sizes">
            <div className="flex items-end gap-8 text-primary">
              <div className="text-center">
                <Spinner size="sm" />
                <span className="block mt-2 font-mono text-xs text-muted-foreground">sm</span>
              </div>
              <div className="text-center">
                <Spinner size="md" />
                <span className="block mt-2 font-mono text-xs text-muted-foreground">md</span>
              </div>
              <div className="text-center">
                <Spinner size="lg" />
                <span className="block mt-2 font-mono text-xs text-muted-foreground">lg</span>
              </div>
            </div>
          </SubBlock>
        </Section>

        {/* ============================== OVERLAYS ============================== */}
        <Section
          id="overlays"
          title="overlays"
          subtitle="Modals, sheets and other overlays. Click to open — they portal into the document body."
        >
          <div className="flex flex-wrap gap-3">
            <Button onClick={() => setModalOpen(true)}>Open modal</Button>
            <Button variant="secondary" onClick={() => setTranscriptOpen(true)}>
              Open transcript modal
            </Button>
          </div>

          <Modal
            isOpen={modalOpen}
            onClose={() => setModalOpen(false)}
            title="Modal — example"
            maxWidth="md"
          >
            <p className="text-sm text-muted-foreground mb-4">
              The modal portals to the body, locks scroll on the page beneath, and closes on
              <code className="font-mono mx-1">Escape</code> or backdrop click.
            </p>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="secondary" onClick={() => setModalOpen(false)}>
                Cancel
              </Button>
              <Button onClick={() => setModalOpen(false)}>Confirm</Button>
            </div>
          </Modal>

          <TranscriptModal
            isOpen={transcriptOpen}
            onClose={() => setTranscriptOpen(false)}
            title="Transcript — sample edition"
            transcript={sampleTranscript}
          />
        </Section>

        {/* ============================== CONVERSION ============================== */}
        <Section
          id="conversion"
          title="conversion"
          subtitle="The URL submit form — the front door to a new edition."
        >
          <div className="max-w-xl">
            <UrlInputForm
              onSubmit={async (url) => {
                // Mock — log and pretend it worked
                console.info('[design-system] submitted URL', url);
                await new Promise((r) => setTimeout(r, 600));
              }}
              creditCost={1}
            />
          </div>
        </Section>

        {/* ============================== CREDITS ============================== */}
        <Section
          id="credits"
          title="credits"
          subtitle="Balance display and credit-pack cards. Popular pack carries LED-orange border."
        >
          <SubBlock label="balance display / led readout — sizes × states">
            <div className="bg-card border border-border rounded-sm p-6 space-y-6">
              <div>
                <p className="font-mono text-xs tracking-[0.18em] uppercase text-muted-foreground mb-4">
                  sizes · sm · md · lg
                </p>
                <div className="flex items-end gap-10 flex-wrap">
                  <CreditBalance balance={42} size="sm" />
                  <CreditBalance balance={42} size="md" />
                  <CreditBalance balance={42} size="lg" />
                </div>
              </div>
              <div className="pt-5 border-t border-border">
                <p className="font-mono text-xs tracking-[0.18em] uppercase text-muted-foreground mb-4">
                  states · ok · low · empty
                </p>
                <div className="flex items-baseline gap-10 flex-wrap">
                  <CreditBalance balance={42} size="md" />
                  <CreditBalance balance={2} size="md" />
                  <CreditBalance balance={0} size="md" />
                </div>
              </div>
              <div className="pt-5 border-t border-border">
                <p className="font-mono text-xs tracking-[0.18em] uppercase text-muted-foreground mb-4">
                  with buy-link
                </p>
                <CreditBalance balance={3} size="md" showBuyLink />
              </div>
            </div>
            <p className="mt-5 max-w-2xl font-mono text-xs leading-relaxed text-muted-foreground">
              The number is rendered as a digital LED readout — mono,
              tabular-nums, with a coloured text-shadow that glows in the
              state colour. Green when there&rsquo;s headroom, amber as a
              warning (≤3), red when empty.
            </p>
          </SubBlock>

          <SubBlock label="credit packs">
            <div className="grid md:grid-cols-3 gap-4">
              {samplePacks.map((pack) => (
                <CreditPackCard
                  key={pack.id}
                  pack={pack}
                  onPurchaseStart={() => console.info('[design-system] purchase start', pack.id)}
                />
              ))}
            </div>
          </SubBlock>
        </Section>

        {/* ============================== FEED ============================== */}
        <Section
          id="feed"
          title="feed"
          subtitle="Edition card variants and the list that composes them."
        >
          <SubBlock label="edition card / states">
            <div className="grid md:grid-cols-2 gap-3">
              <EditionCard
                edition={{ ...sampleEdition, status: 'ready' }}
                editionType="Personal edition"
                onPlay={(id) => console.info('play', id)}
                onDelete={(id) => console.info('delete', id)}
                onViewTranscript={(id) => console.info('transcript', id)}
                onShare={async (id): Promise<ShareResult> => {
                  console.info('share', id);
                  return 'copied' as const;
                }}
                onDownload={async (id) => console.info('download', id)}
              />
              <EditionCard
                edition={{ ...sampleEdition, id: 'now-playing', status: 'ready' }}
                isActive
                editionType="Personal edition"
                onPlay={() => undefined}
                onDelete={() => undefined}
                onViewTranscript={() => undefined}
                onShare={async () => 'shared' as const}
                onDownload={async () => undefined}
              />
              <EditionCard
                edition={{
                  ...sampleEdition,
                  id: 'processing',
                  status: 'processing',
                  remainingSeconds: undefined,
                }}
                onPlay={() => undefined}
                onDelete={() => undefined}
              />
              <EditionCard
                edition={{
                  ...sampleEdition,
                  id: 'queued',
                  status: 'queued',
                  remainingSeconds: undefined,
                }}
                onPlay={() => undefined}
                onDelete={() => undefined}
              />
            </div>
          </SubBlock>

          <SubBlock label="library list / with mock data">
            <LibraryList
              conversions={sampleConversions}
              total={sampleConversions.length}
              currentPage={1}
              pageSize={10}
              playingId={null}
              onPlay={(c) => console.info('list play', c.id)}
              onDelete={async (id) => {
                console.info('list delete', id);
              }}
            />
          </SubBlock>

          <SubBlock label="edition card / standalone (Edition prop API)">
            <div className="space-y-3 max-w-3xl">
              {sampleEditions.map((e) => (
                <EditionCard
                  key={e.id}
                  edition={e}
                  onPlay={() => undefined}
                  onDelete={() => undefined}
                />
              ))}
            </div>
          </SubBlock>
        </Section>

        {/* ============================== FEEDS / SUMMARY CARDS ============================== */}
        <Section
          id="feeds"
          title="feeds"
          subtitle="Feed management — status badges and the row card used on the feeds page."
        >
          <SubBlock label="status badge / led tones">
            <div className="bg-card border border-border rounded-sm p-6">
              <div className="flex flex-wrap items-center gap-3">
                <StatusBadge tone="ok">active</StatusBadge>
                <StatusBadge tone="ok">published</StatusBadge>
                <StatusBadge tone="live">live</StatusBadge>
                <StatusBadge tone="warn">syncing</StatusBadge>
                <StatusBadge tone="error">failed</StatusBadge>
                <StatusBadge tone="off">draft</StatusBadge>
              </div>
              <p className="mt-5 max-w-2xl font-mono text-xs leading-relaxed text-muted-foreground">
                Coloured LED dot + uppercase mono label, wrapped in a hairline-bordered pill.
                Tones map to the field-system LEDs:{' '}
                <span className="text-foreground">ok</span> (green, signal present),{' '}
                <span className="text-foreground">live</span> (orange, active),{' '}
                <span className="text-foreground">warn</span> (amber, attention),{' '}
                <span className="text-foreground">error</span> (red, failure),{' '}
                <span className="text-foreground">off</span> (steel, inactive).
              </p>
            </div>
          </SubBlock>

          <SubBlock label="feed summary card / variants">
            <div className="space-y-3 max-w-3xl">
              <FeedSummaryCard
                name="Default Feed"
                description="Your personal podcast feed. Available in any podcast app."
                badge={{ label: 'active', tone: 'ok' }}
                onOpen={() => console.info('[design-system] open default feed')}
              />
              <FeedSummaryCard
                name="Weekly reading list"
                description="A curated rotation of long-reads — engineering, design, and the occasional essay."
                badge={{ label: 'published', tone: 'ok' }}
                itemCount={12}
                onOpen={() => console.info('[design-system] open weekly')}
                onDelete={() => console.info('[design-system] delete weekly')}
              />
              <FeedSummaryCard
                name="Q4 planning bundle"
                description="Drafts only — not yet shared with the team."
                badge={{ label: 'draft', tone: 'off' }}
                itemCount={3}
                onOpen={() => console.info('[design-system] open draft')}
                onDelete={() => console.info('[design-system] delete draft')}
              />
              <FeedSummaryCard
                name="One-off — no actions"
                description="A summary card without open/delete affordances, for read-only contexts."
                badge={{ label: 'live', tone: 'live' }}
                itemCount={1}
              />
            </div>
          </SubBlock>
        </Section>

        {/* ============================== LIBRARY / AUDIO PLAYER ============================== */}
        <Section
          id="library"
          title="library"
          subtitle="The transport. Fixed to the bottom of the viewport in the live app — toggle below to mount it."
        >
          <div className="flex flex-wrap items-center gap-3">
            <Button onClick={() => setPlayerOpen((p) => !p)}>
              {playerOpen ? 'Hide audio player' : 'Show audio player'}
            </Button>
            <span className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
              {playerOpen ? 'mounted at viewport bottom' : 'hidden'}
            </span>
          </div>
          <div className="mt-4 text-sm text-muted-foreground max-w-xl">
            The player loads metadata from the URL — with no real audio src it renders the empty
            time / progress state so you can preview the chrome.
          </div>
        </Section>
      </main>

      {/* Audio player — fixed bottom when toggled on */}
      {playerOpen && (
        <AudioPlayer
          src=""
          title="The hidden cost of platform teams that can't say no"
          subtitle="The Pragmatic Engineer · NCT-042"
          onClose={() => setPlayerOpen(false)}
        />
      )}
    </div>
  );
}

function TypeRow({
  label,
  demoClass,
  children,
}: {
  label: string;
  demoClass: string;
  children: React.ReactNode;
}) {
  return (
    <div className="px-5 py-5 flex items-baseline justify-between gap-6 flex-wrap">
      <div className={demoClass}>{children}</div>
      <span className="font-mono text-xs uppercase tracking-[0.14em] text-muted-foreground whitespace-nowrap">
        {label}
      </span>
    </div>
  );
}

export default DesignSystemPage;
